<template>
  <page-bar title="Groceries">
    <v-btn
      icon="mdi-check-all"
      @click="$emit('clear')"
      aria-label="clear all"
    />
  </page-bar>
  <!-- Lines stay readable: Material 3 caps text content at 840px -->
  <v-container max-width="840">
    <v-card>
      <v-list>
        <!-- Ticked items move down to a Done section -->
        <template v-for="(grocery, index) in ordered" :key="grocery.id">
          <v-list-subheader v-if="index === firstDone">Done</v-list-subheader>
          <v-list-item
            density="compact"
            :class="{ 'grocery--done': grocery.done }"
          >
            <template v-slot:prepend>
              <v-list-item-action start>
                <v-checkbox-btn
                  :name="`item-${index + 1}`"
                  :aria-label="`grocery item ${index + 1} done`"
                  :model-value="grocery.done"
                  @update:model-value="
                    (done: boolean | null) =>
                      $emit('update', grocery.id, { done: !!done })
                  "
                ></v-checkbox-btn>
              </v-list-item-action>
            </template>

            <v-list-item-title>
              <!-- Saved on change (blur or enter), not on every keystroke -->
              <v-text-field
                :aria-label="`grocery item ${index + 1}`"
                :placeholder="`grocery item ${index + 1}`"
                :model-value="grocery.text"
                :suffix="quantity(grocery)"
                @change="
                  (event: Event) =>
                    $emit('update', grocery.id, {
                      text: (event.target as HTMLInputElement).value,
                    })
                "
                variant="plain"
                density="compact"
                hide-details="auto"
                class="dense"
                autofocus
              />
            </v-list-item-title>
            <template v-slot:append>
              <v-list-item-action end>
                <v-icon
                  icon="mdi-close"
                  aria-label="remove"
                  @click="$emit('remove', grocery.id)"
                />
              </v-list-item-action>
            </template>
          </v-list-item>
        </template>
        <v-list-item @click="$emit('add')">
          <template v-slot:prepend>
            <v-list-item-action start>
              <v-icon icon="mdi-plus" aria-label="add"></v-icon>
            </v-list-item-action>
          </template>
          <v-list-item-title>Add grocery item</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-card>
  </v-container>
</template>

<script lang="ts">
  import { defineComponent, type PropType } from 'vue';
  import PageBar from '@/components/infrastructure/page-bar/PageBar.vue';
  import type { Grocery, GroceryChanges } from '@/service/groceries/types';
  import { formatQuantity, type UnitSystem } from '@/service/recipe/units';

  export default defineComponent({
    name: 'GroceriesView',
    components: { PageBar },
    props: {
      unitSystem: {
        type: String as PropType<UnitSystem>,
        default: 'original',
      },
      groceries: {
        type: Array as PropType<Grocery[]>,
        required: true,
      },
    },
    computed: {
      // To buy first, then the ticked items, each in the order they were added
      ordered(): Grocery[] {
        return [
          ...this.groceries.filter(({ done }) => !done),
          ...this.groceries.filter(({ done }) => done),
        ];
      },
      firstDone(): number {
        return this.ordered.findIndex(({ done }) => done);
      },
    },
    methods: {
      // "1 ½ Cup(s)", "300 Gram"; nothing for free-text items
      quantity({ amount, unit }: Grocery): string {
        return formatQuantity(amount ?? 0, unit ?? '', this.unitSystem);
      },
    },
    emits: {
      add: () => true,
      update: (_id: string, _changes: GroceryChanges) => true,
      remove: (_id: string) => true,
      clear: () => true,
    },
  });
</script>

<style>
  .dense input {
    padding-top: 4px !important;
  }

  .grocery--done input {
    text-decoration: line-through;
  }
</style>
